import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReviewType } from '@wineryxplorer/shared';
import { ReviewCardActions } from './ReviewCardActions';
import { ReviewPhotos } from './ReviewPhotos';
import { StarDisplay, WineScoreDisplay, RatingBreakdown } from './ReviewRatingDisplays';

// Serialized review type from API (with userName instead of userId lookup)
export interface SerializedReview {
  _id: string;
  userId: string;
  userName: string;
  /** True when the author erased their account; `userName` is then "Member" (#380). */
  authorDeleted?: boolean;
  title: string;
  body: string;
  imageIds: string[];
  helpfulCount: number;
  notHelpfulCount: number;
  flaggedForModeration: boolean;
  response?: {
    responseBody: string;
    respondedAt: Date | string;
  };
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface SerializedVenueReview extends SerializedReview {
  venueId: string;
  ratings: {
    serviceAndStaff: number;
    facility: number;
    wines: number;
    value: number;
    overall: number;
  };
}

export interface SerializedEventReview extends SerializedReview {
  eventId: string;
  ratings: {
    serviceAndStaff: number;
    location: number;
    wines: number;
    food?: number;
    value: number;
    overall: number;
  };
}

export interface SerializedExperienceReview extends SerializedReview {
  experienceId: string;
  ratings: {
    serviceAndStaff: number;
    location: number;
    wines: number;
    food?: number;
    value: number;
    overall: number;
  };
}

export interface SerializedWineReview extends SerializedReview {
  wineId: string;
  ratings: {
    score: number;
    valueForMoney: number;
  };
}

export type AnySerializedReview =
  | SerializedVenueReview
  | SerializedEventReview
  | SerializedExperienceReview
  | SerializedWineReview;

export interface ReviewCardProps {
  review: AnySerializedReview;
  reviewType: ReviewType;
  onVote?: (reviewId: string, voteType: 'helpful' | 'notHelpful') => void;
  onFlag?: (reviewId: string, flagType: 'OFFENSIVE' | 'INAPPROPRIATE' | 'FAKE') => void;
  onVenueManagerFlag?: (reviewId: string) => void;
  isVenueManager?: boolean;
  userVoteStatus?: { hasVoted: boolean; voteType: 'helpful' | 'notHelpful' | null };
  isVoting?: boolean;
  showActions?: boolean;
  isOwnReview?: boolean;
  className?: string;
}

// Helper to format date
const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const ReviewCard = ({
  review,
  reviewType,
  onVote,
  onFlag,
  onVenueManagerFlag,
  isVenueManager = false,
  userVoteStatus,
  isVoting = false,
  showActions = true,
  isOwnReview = false,
  className = '',
}: ReviewCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isWineReview = reviewType === 'wine';
  const wineReview = isWineReview ? (review as SerializedWineReview) : null;
  const standardReview = !isWineReview
    ? (review as SerializedVenueReview | SerializedEventReview | SerializedExperienceReview)
    : null;

  // Truncate body text for preview
  const maxPreviewLength = 300;
  const shouldTruncate = review.body.length > maxPreviewLength;
  const displayBody =
    shouldTruncate && !isExpanded ? `${review.body.slice(0, maxPreviewLength)}...` : review.body;

  return (
    <article
      className={`bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 ${className}`}
      aria-labelledby={review.title ? `review-title-${review._id}` : undefined}
    >
      {/* Header: Username, date, and flagged badge */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {review.authorDeleted ? (
            <span className="font-medium text-gray-900">{review.userName}</span>
          ) : (
            <Link
              to={`/users/${review.userName}`}
              className="font-medium text-gray-900 hover:text-primary-700 hover:underline"
            >
              {review.userName}
            </Link>
          )}
          <span className="text-gray-400">•</span>
          <time
            className="text-sm text-gray-500"
            dateTime={new Date(review.createdAt).toISOString()}
          >
            {formatDate(review.createdAt)}
          </time>
        </div>
        {review.flaggedForModeration && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Flagged for Moderation
          </span>
        )}
      </div>

      {/* Overall rating or wine score */}
      <div className="mb-3">
        {isWineReview && wineReview ? (
          <WineScoreDisplay
            score={wineReview.ratings.score}
            valueForMoney={wineReview.ratings.valueForMoney}
          />
        ) : standardReview ? (
          <StarDisplay value={standardReview.ratings.overall} size="md" />
        ) : null}
      </div>

      {/* Title — empty once an erased author's review is anonymized */}
      {review.title && (
        <h3 id={`review-title-${review._id}`} className="text-lg font-semibold text-gray-900 mb-2">
          {review.title}
        </h3>
      )}

      {/* Rating breakdown (for non-wine reviews) */}
      {!isWineReview && standardReview && (
        <div className="mb-3">
          <RatingBreakdown ratings={standardReview.ratings} reviewType={reviewType} />
        </div>
      )}

      {/* Body text */}
      {review.body && (
        <div className="mb-4">
          <p className="text-gray-700 whitespace-pre-wrap">{displayBody}</p>
          {shouldTruncate && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}

      {/* Photos */}
      {review.imageIds.length > 0 && <ReviewPhotos imageIds={review.imageIds} />}

      {/* Venue Response — nested reply, expressed via indentation +
          layered paper surface, not a side-stripe border */}
      {review.response && (
        <div className="mb-4 ml-6 md:ml-10 bg-gray-100 border border-gray-200 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-600">
              Response from Venue
            </span>
            <span className="text-sm text-gray-500">{formatDate(review.response.respondedAt)}</span>
          </div>
          <p className="text-gray-700">{review.response.responseBody}</p>
        </div>
      )}

      {/* Actions: Helpfulness voting and flagging */}
      {showActions && (
        <ReviewCardActions
          reviewId={review._id}
          helpfulCount={review.helpfulCount}
          notHelpfulCount={review.notHelpfulCount}
          flaggedForModeration={review.flaggedForModeration}
          onVote={onVote}
          onFlag={onFlag}
          onVenueManagerFlag={onVenueManagerFlag}
          isVenueManager={isVenueManager}
          userVoteStatus={userVoteStatus}
          isVoting={isVoting}
          isOwnReview={isOwnReview}
        />
      )}
    </article>
  );
};
